import { dateLocale } from "@/lib/dateTime";
import type { ContactRecord } from "@/types";
import { CallLink } from "@/components/calling/CallLink";

const Card = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="flex flex-col gap-1 rounded-lg border border-border bg-card px-3.5 py-3">
    <h2 className="mb-1 text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
    {children}
  </section>
);
const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col border-b border-border/60 py-1.5 last:border-b-0">
    <span className="text-[11.5px] text-muted-foreground">{label}</span>
    <span className="break-words text-[13px] font-medium">{children}</span>
  </div>
);
const link = "text-primary hover:underline";
const day = (d?: string | null) => (d ? new Date(d).toLocaleDateString(dateLocale(), { day: "numeric", month: "short", year: "numeric" }) : null);

/** How to reach the person, and a little about them. Empty fields are left out. */
export function ContactProperties({ contact, ownerName, cameFrom }: { contact: ContactRecord; ownerName: string; cameFrom?: string | null }) {
  const reach: [string, React.ReactNode][] = [
    ["Mobile · WhatsApp", contact.mobile && <CallLink type="CONTACT" id={contact.id} field="mobile" number={contact.mobile} className={link}>{contact.mobile}</CallLink>],
    ["Office phone", contact.phone && <CallLink type="CONTACT" id={contact.id} field="phone" number={contact.phone} className={link}>{contact.phone}</CallLink>],
    ["Email", contact.email && <a href={`mailto:${contact.email}`} className={link}>{contact.email}</a>],
  ];
  const about: [string, React.ReactNode][] = [
    ["Owner", ownerName],
    ["Job title", contact.job_title],
    ["Came from", cameFrom],
    ["Added", day(contact.created_on)],
  ];
  const shown = (rows: [string, React.ReactNode][]) => rows.filter(([, v]) => v);
  return (
    <div className="flex flex-col gap-3">
      {[["Reach", shown(reach)], ["About", shown(about)]].map(([title, rows]) =>
        (rows as [string, React.ReactNode][]).length ? (
          <Card key={title as string} title={title as string}>
            {(rows as [string, React.ReactNode][]).map(([label, value]) => (
              <Row key={label} label={label}>
                {value}
              </Row>
            ))}
          </Card>
        ) : null,
      )}
    </div>
  );
}
